/**
 * `useAskUserFromAgent` — subscribes to the `lx_ask_user` broadcast (Phase A).
 *
 * Plan: `ai-docs/upload-intent-and-ingest-closing-loop-miniplan.md` §4/§4-bis.
 * Contract + parser: `@/lib/ask-user`. Surface: `@/components/workspace/AskUserWizard`
 * (the Phase A AskUserCard was replaced by the ratified wizard redesign,
 * lexide-ask-user-wizard-redesign-miniplan.md).
 *
 * The runtime tool is a near-no-op; the card rides OpenClaw core's automatic
 * `session.tool` EventFrame (`{phase:"start", name|toolName, args|toolInput}`),
 * exactly like the shipped `use*CommandsFromAgent` hooks. No core change, no
 * new RPC.
 *
 * ⚠ **Both field spellings are accepted, and this is not defensive padding.**
 * The live broadcast ships `data.name` + `data.args`; the `toolName`/`toolInput`
 * spelling is what message-content blocks get rekeyed into. Five sibling
 * listeners read only the legacy pair and miss the broadcast SILENTLY — the
 * documented trap in `useTeamPaneCommandsFromAgent`'s header. `parseAskUserEvent`
 * accepts both.
 *
 * ## One card at a time
 *
 * A second ask REPLACES the first. Rationale: the questions come from one turn
 * of reasoning; stacking cards would leave the lawyer answering a stale ask
 * whose context the agent has already moved past, and the agent — which is not
 * waiting (A-0.2) — has no way to reconcile two overlapping answer sets.
 */

import { useCallback, useRef, useState } from "react";
import { useEventStream } from "@/hooks/useEventStream";
import {
  askBelongsToSession,
  parseAskUserEvent,
  withAskIdentity,
  type AskUserCardData,
} from "@/lib/ask-user";

export interface UseAskUserFromAgentResult {
  /** The live card data, or null when nothing is being asked. */
  card: AskUserCardData | null;
  /** Clear the card (after submit, or on dismiss). */
  clear: () => void;
}

export function useAskUserFromAgent(
  /**
   * The session this composer speaks for. An ask for any OTHER session is
   * IGNORED — see `askBelongsToSession` for why rendering it would misdeliver
   * the answer. Pass `undefined` only if the caller genuinely has no scope, in
   * which case nothing renders (fail-closed).
   */
  currentSessionKey: string | undefined,
): UseAskUserFromAgentResult {
  const [card, setCard] = useState<AskUserCardData | null>(null);
  /**
   * Local sequence, used ONLY when the wire omits `toolCallId`.
   *
   * This is a render identity, not data standing in for a missing fact: the
   * card must be a fresh component per ask (see `AskUserCardData.askId`), and
   * without SOME distinct value two consecutive asks silently share state. It
   * is deliberately not persisted or sent anywhere.
   */
  const seq = useRef(0);

  useEventStream("session.tool", (payload) => {
    const parsed = parseAskUserEvent(payload);
    // `null` covers every non-ask event AND malformed asks — rendering a
    // broken card is worse than rendering none, since no user action fixes it
    // and the model's own text still arrives in the thread.
    if (!parsed) return;
    // Scope gate BEFORE the sequence bump, so an out-of-scope ask does not
    // consume a local id (which would make the ids lie about how many asks
    // this composer actually received).
    if (!askBelongsToSession(parsed, currentSessionKey)) return;
    seq.current += 1;
    setCard(withAskIdentity(parsed, seq.current));
  });

  const clear = useCallback(() => setCard(null), []);
  return { card, clear };
}
